import { useState } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { Icon } from "../components/icons";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Table,
  Tag,
  Td,
  Th,
  buttonStyles,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import { chargedMoney } from "../lib/money";
import { useCurrencies, useSetFxPolicy, useSetFxRate, useSyncFxRates } from "../lib/queries";
import type { FxRate, FxRateHistoryEntry } from "../lib/types";

/**
 * The price of a dollar of credit in every currency the platform presents in.
 *
 * Its own page rather than a card on Revenue, because it is operated, not
 * only read: a superadmin sets the margin, chooses whether the rate follows
 * the market, types a value by hand when it must, and looks back through
 * every value the rate has held. Revenue keeps one line per currency and a
 * link here.
 *
 * Per currency: the rate in force and where it came from -- the market
 * plus a margin, the console, the deployment's var, or the code's default
 * -- the ECB reference the daily sync last fetched and how far the rate
 * sits from it, the two actions, and the history in full.
 *
 * The rate is the platform's price. The market is an input to it, never
 * the price itself: a currency in auto mode is re-derived once a day from
 * the reference plus the margin set here, and a manual one shows the
 * reference beside the typed value as the thing to compare against. Both
 * keep the two guards the API enforces: a value outside the currency's
 * band is refused as a slipped digit, and a move of more than a quarter --
 * typed or derived -- waits for a person to confirm it.
 */
export function CurrenciesPage() {
  const { role } = useAuth();
  const canWrite = role === "superadmin";
  const { data, isPending, error } = useCurrencies();
  const [editing, setEditing] = useState<FxRate | null>(null);
  const [policy, setPolicy] = useState<FxRate | null>(null);
  const sync = useSyncFxRates();

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  return (
    <>
      <PageHeader
        title="Currencies"
        description={`What a ${data.base} of credit is charged at where a processor takes another currency. The platform's price, recorded on every payment it is used for; the market reference is fetched once a day.`}
        action={
          <div className="flex gap-2">
            <Link to="/revenue" className={buttonStyles("secondary", "sm")}>
              Revenue
            </Link>
            {canWrite && (
              <Button variant="secondary" size="sm" loading={sync.isPending} onClick={() => sync.mutate()}>
                Fetch reference now
              </Button>
            )}
          </div>
        }
      />
      {sync.error && <Alert>{sync.error.message}</Alert>}
      {sync.data && (
        <Alert tone="info">
          {sync.data.results
            .map((r) =>
              r.error
                ? `${r.currency}: ${r.error}`
                : r.applied
                  ? `${r.currency}: rate moved from ${chargedMoney(r.applied.from, r.currency)} to ${chargedMoney(r.applied.to, r.currency)}`
                  : `${r.currency}: reference ${r.reference ? chargedMoney(r.reference.rate, r.currency) : "—"} fetched, rate unchanged`,
            )
            .join(" · ")}
        </Alert>
      )}

      {data.rates.map((rate) => (
        <CurrencyPanel
          key={rate.currency}
          rate={rate}
          base={data.base}
          staleAfterHours={data.staleAfterHours}
          history={data.history[rate.currency] ?? []}
          canWrite={canWrite}
          onEdit={() => setEditing(rate)}
          onPolicy={() => setPolicy(rate)}
        />
      ))}

      {editing && <SetRateModal rate={editing} base={data.base} confirmAbove={data.confirmAbove} onClose={() => setEditing(null)} />}
      {policy && <PolicyModal rate={policy} base={data.base} confirmAbove={data.confirmAbove} onClose={() => setPolicy(null)} />}
    </>
  );
}

function CurrencyPanel({
  rate,
  base,
  staleAfterHours,
  history,
  canWrite,
  onEdit,
  onPolicy,
}: {
  rate: FxRate;
  base: string;
  staleAfterHours: number;
  history: FxRateHistoryEntry[];
  canWrite: boolean;
  onEdit: () => void;
  onPolicy: () => void;
}) {
  const reference = rate.reference;
  const ageHours = reference ? (Date.now() - new Date(reference.fetchedAt).getTime()) / 36e5 : null;
  const stale = ageHours !== null && ageHours > staleAfterHours;
  const effectiveMargin = reference ? ((rate.rate - reference.rate) / reference.rate) * 100 : null;

  return (
    <Card>
      <CardHeader
        title={`${rate.currency} per ${base}`}
        description={
          rate.source === "market" && rate.updatedAt
            ? `Following the market. Last derived ${formatRelative(rate.updatedAt)}${rate.note ? ` — ${rate.note}` : ""}.`
            : rate.source === "console" && rate.updatedAt
              ? `Set by hand ${formatRelative(rate.updatedAt)}${rate.updatedBy?.email ? ` by ${rate.updatedBy.email}` : ""}${rate.note ? ` — ${rate.note}` : ""}.`
              : rate.source === "deployment"
                ? "From the deployment's configuration. Nobody has set it here yet."
                : "The code's default. Nobody has set it here yet."
        }
        action={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={onPolicy}>
                {rate.mode === "auto" ? "Margin & mode" : "Follow the market"}
              </Button>
              <Button variant="secondary" size="sm" onClick={onEdit}>
                Set by hand
              </Button>
            </div>
          ) : undefined
        }
      />

      <section className="grid gap-px bg-line sm:grid-cols-3">
        <div className="bg-surface px-5 py-4">
          <p className="text-sm font-medium text-fg-subtle">Rate in force</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-fg">{chargedMoney(rate.rate, rate.currency)}</p>
          <p className="mt-1 text-xs text-fg-subtle">
            <SourceTag source={rate.source} marginPercent={rate.marginPercent} />
          </p>
          <p className="mt-2 border-t border-line pt-2 text-xs text-fg-subtle">Read at every checkout and written onto the payment.</p>
        </div>
        <div className="bg-surface px-5 py-4">
          <p className="text-sm font-medium text-fg-subtle">Market reference</p>
          <p className={cx("mt-2 text-2xl font-semibold tracking-tight tabular-nums", stale ? "text-warn" : "text-fg")}>
            {reference ? chargedMoney(reference.rate, rate.currency) : "—"}
          </p>
          <p className="mt-1 text-xs text-fg-subtle">
            {reference
              ? `${reference.source}, fixing of ${reference.asOf} · fetched ${formatRelative(reference.fetchedAt)}${stale ? " — stale" : ""}`
              : "Not fetched yet. The daily sync fills this in; \"Fetch reference now\" does it today."}
          </p>
          <p className="mt-2 border-t border-line pt-2 text-xs text-fg-subtle">A reference, not a dealing rate: an input to the price, never the price.</p>
        </div>
        <div className="bg-surface px-5 py-4">
          <p className="text-sm font-medium text-fg-subtle">{rate.mode === "auto" ? "Margin" : "Effective margin"}</p>
          <p
            className={cx(
              "mt-2 text-2xl font-semibold tracking-tight tabular-nums",
              effectiveMargin !== null && effectiveMargin < 0 ? "text-danger" : "text-fg",
            )}
          >
            {rate.mode === "auto"
              ? `+${rate.marginPercent}%`
              : effectiveMargin !== null
                ? `${effectiveMargin >= 0 ? "+" : ""}${effectiveMargin.toFixed(1)}%`
                : "—"}
          </p>
          <p className="mt-1 text-xs text-fg-subtle">
            {rate.mode === "auto"
              ? `Over the reference, rounded up to ${chargedMoney(100, rate.currency)}${effectiveMargin !== null ? ` · ${effectiveMargin.toFixed(1)}% in effect` : ""}`
              : effectiveMargin !== null
                ? effectiveMargin < 0
                  ? "Under the market: every top-up loses money at this rate"
                  : "How far the typed rate sits over the market"
                : "Needs a reference to compare against"}
          </p>
          <p className="mt-2 border-t border-line pt-2 text-xs text-fg-subtle">Where the spread, the processor's fee and the rounding live.</p>
        </div>
      </section>

      {rate.syncError && (
        <div className="border-t border-line px-5 py-3">
          <p className="flex items-start gap-1.5 text-xs text-warn">
            <Icon name="warning" className="mt-0.5 size-3.5 shrink-0" />
            {rate.syncError}
          </p>
        </div>
      )}

      {history.length > 0 && (
        <Table
          head={
            <tr>
              <Th priority="sm">When</Th>
              <Th numeric>Rate</Th>
              <Th numeric priority="sm">From</Th>
              <Th>By</Th>
              <Th>Note</Th>
            </tr>
          }
        >
          {history.map((entry) => (
            <tr key={entry.id}>
              <Td className="text-fg-subtle" priority="sm">
                {formatDateTime(entry.createdAt)}
              </Td>
              <Td numeric>{chargedMoney(entry.rate, entry.currency)}</Td>
              <Td numeric className="text-fg-subtle" priority="sm">
                {entry.previousRate !== null ? chargedMoney(entry.previousRate, entry.currency) : "—"}
              </Td>
              <Td className="text-fg-subtle">{entry.setBy?.email ?? "market sync"}</Td>
              <Td className="max-w-md text-xs text-fg-muted">{entry.note ?? ""}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

function SourceTag({ source, marginPercent }: { source: FxRate["source"]; marginPercent: number }) {
  if (source === "console") return <Tag tone="neutral">set by hand</Tag>;
  if (source === "market") return <Tag tone="good">market +{marginPercent}%</Tag>;
  return <Tag tone="warn">{source === "deployment" ? "deployment default" : "code default"}</Tag>;
}

/**
 * Manual or market-following, and the margin. Switching to auto derives the
 * rate at once; a derived value over a quarter away comes back as the
 * question, the same as a typed one.
 */
function PolicyModal({ rate, base, confirmAbove, onClose }: { rate: FxRate; base: string; confirmAbove: number; onClose: () => void }) {
  const [mode, setMode] = useState<FxRate["mode"]>(rate.mode);
  const [margin, setMargin] = useState(String(rate.marginPercent));
  const [confirming, setConfirming] = useState<{ previous: number; rate: number | null } | null>(null);
  const mutation = useSetFxPolicy();
  const parsed = Number(margin);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 50;

  function submit(confirm: boolean) {
    mutation.mutate(
      { currency: rate.currency, mode, marginPercent: parsed, confirm },
      {
        onSuccess: onClose,
        onError: (error) => {
          const details = error instanceof ApiError ? (error.details as { confirmRequired?: boolean; previous?: number; rate?: number | null } | undefined) : undefined;
          if (details?.confirmRequired) setConfirming({ previous: details.previous ?? rate.rate, rate: details.rate ?? null });
        },
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${rate.currency}: how the rate is kept`}
      description="Following the market re-derives the rate once a day from the ECB reference plus the margin. Typing a rate by hand switches back to manual."
      dismissable={!mutation.isPending}
    >
      <div className="space-y-4">
        {mutation.error && !confirming && <Alert>{mutation.error.message}</Alert>}
        {confirming && (
          <Alert tone="warning">
            <span className="flex items-start gap-2">
              <Icon name="warning" className="mt-0.5 size-4 shrink-0" />
              <span>
                Following the market would move the rate from {chargedMoney(confirming.previous, rate.currency)}
                {confirming.rate !== null ? ` to ${chargedMoney(confirming.rate, rate.currency)}` : ""} per {base} — more than{" "}
                {Math.round(confirmAbove * 100)}%. Apply it only if the market really moved that far.
              </span>
            </span>
          </Alert>
        )}
        <div className="flex gap-2" role="radiogroup" aria-label="Mode">
          {(
            [
              ["auto", "Follow the market"],
              ["manual", "Keep as typed"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              disabled={Boolean(confirming)}
              onClick={() => setMode(value)}
              className={
                mode === value
                  ? "rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs text-fg"
                  : "rounded-md border border-line px-3 py-1.5 text-xs text-fg-muted hover:border-line-strong hover:text-fg"
              }
            >
              {label}
            </button>
          ))}
        </div>
        <Field
          label="Margin over the reference"
          htmlFor="fx-margin"
          hint="Percent. Where the spread, the processor's fee and the rounding live; 0 to 50. Used in auto mode, and shown in manual mode for comparison."
        >
          <Input
            id="fx-margin"
            inputMode="decimal"
            value={margin}
            disabled={Boolean(confirming)}
            onChange={(event) => {
              if (/^\d{0,2}(\.\d{0,3})?$/.test(event.target.value)) setMargin(event.target.value);
            }}
          />
        </Field>
        {rate.reference && mode === "auto" && valid && (
          <p className="text-xs text-fg-subtle">
            From {chargedMoney(rate.reference.rate, rate.currency)} ({rate.reference.asOf}) that is about{" "}
            {chargedMoney(Math.ceil((rate.reference.rate * (1 + parsed / 100)) / 100) * 100, rate.currency)} per {base}.
          </p>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={confirming ? () => setConfirming(null) : onClose} disabled={mutation.isPending}>
            {confirming ? "Back" : "Cancel"}
          </Button>
          <Button onClick={() => submit(Boolean(confirming))} disabled={!valid} loading={mutation.isPending}>
            {confirming ? "Apply anyway" : mode === "auto" ? "Follow the market" : "Keep manual"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The edit, with the confirmation folded in: a first submit that the API
 * answers "confirm" turns the dialog into the question, showing the move it
 * is about to make, and the second submit carries `confirm: true`.
 */
function SetRateModal({
  rate,
  base,
  confirmAbove,
  onClose,
}: {
  rate: FxRate;
  base: string;
  confirmAbove: number;
  onClose: () => void;
}) {
  const [value, setValue] = useState(String(rate.rate));
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<{ previous: number; changePercent: number } | null>(null);
  const mutation = useSetFxRate();

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed > 0;

  function submit(confirm: boolean) {
    if (!valid) return;
    mutation.mutate(
      { currency: rate.currency, rate: parsed, note: note.trim() || undefined, confirm },
      {
        onSuccess: onClose,
        onError: (error) => {
          const details = error instanceof ApiError ? (error.details as { confirmRequired?: boolean; previous?: number; changePercent?: number } | undefined) : undefined;
          if (details?.confirmRequired) setConfirming({ previous: details.previous ?? rate.rate, changePercent: details.changePercent ?? 0 });
        },
      },
    );
  }

  const refusal = mutation.error && !confirming ? mutation.error.message : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${rate.currency} per ${base}`}
      description="Read at checkout and written onto the payment. Changing it never touches a payment already made."
      dismissable={!mutation.isPending}
    >
      <div className="space-y-4">
        {refusal && <Alert>{refusal}</Alert>}

        {confirming ? (
          <Alert tone="warning">
            <span className="flex items-start gap-2">
              <Icon name="warning" className="mt-0.5 size-4 shrink-0" />
              <span>
                That moves the rate by <strong>{confirming.changePercent}%</strong>, from{" "}
                {chargedMoney(confirming.previous, rate.currency)} to {chargedMoney(parsed, rate.currency)} per {base}. A
                move over {Math.round(confirmAbove * 100)}% is usually a slipped digit; apply it only if it is not.
              </span>
            </span>
          </Alert>
        ) : null}

        <Field label="Rate" htmlFor="fx-rate" hint={`How many ${rate.currency} one ${base} of credit is charged at. Whole units.`}>
          <Input
            id="fx-rate"
            inputMode="decimal"
            value={value}
            disabled={Boolean(confirming)}
            onChange={(event) => {
              if (/^\d*\.?\d{0,4}$/.test(event.target.value)) setValue(event.target.value);
            }}
          />
        </Field>
        <Field label="Note" htmlFor="fx-note" hint="Optional. Why it moved -- kept with the history.">
          <Input
            id="fx-note"
            value={note}
            disabled={Boolean(confirming)}
            placeholder="Market moved; kept the margin"
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={confirming ? () => setConfirming(null) : onClose} disabled={mutation.isPending}>
            {confirming ? "Back" : "Cancel"}
          </Button>
          <Button onClick={() => submit(Boolean(confirming))} disabled={!valid} loading={mutation.isPending}>
            {confirming ? "Apply anyway" : "Set rate"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
